import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { sendOtp, verifyOtp } from '../../services/authApi';
import { resolveMirror } from '../../services/mirrorApi';
import { applyOtpDigits, useOtpAutofill } from '../../hooks/useOtpAutofill';
import { getCustomerHomeRoute } from '../../config/constants';
import './OTPVerification.css';

const OTPVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { mirrorId, qrToken, mobile, reqId: initialReqId } = location.state || {};

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [reqId, setReqId] = useState(initialReqId || '');

  useOtpAutofill(setOtp, { enabled: Boolean(mobile && reqId) });

  useEffect(() => {
    if (!mobile || !reqId) {
      const mirrorIdParam = mirrorId ? `?mirrorId=${mirrorId}` : '';
      navigate(`/mobile-login${mirrorIdParam}`, { replace: true });
      return;
    }

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [mobile, reqId, mirrorId, navigate]);

  const requestOTP = async () => {
    try {
      const response = await sendOtp(mobile);
      const nextReqId = response.data?.reqId;
      if (!nextReqId) {
        throw new Error('Failed to send OTP. Please try again.');
      }
      setReqId(nextReqId);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleOTPChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    if (!digit && value.length > 0) return;

    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    if (digit && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleAutofillCapture = useCallback((e) => {
    const filled = applyOtpDigits(e.target.value, setOtp);
    if (filled) {
      e.target.value = '';
      document.getElementById('otp-5')?.focus();
    }
  }, []);

  const handlePaste = (index, e) => {
    const pasted = e.clipboardData?.getData('text') || '';
    if (!pasted) return;
    e.preventDefault();
    const digits = pasted.replace(/\D/g, '');
    if (digits.length >= 6) {
      applyOtpDigits(digits, setOtp);
      document.getElementById('otp-5')?.focus();
      return;
    }
    if (digits.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < digits.length && index + i < 6; i += 1) {
        newOtp[index + i] = digits[i];
      }
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      document.getElementById(`otp-${nextIndex}`)?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  const clearOtpInputs = useCallback(() => {
    setOtp(['', '', '', '', '', '']);
    requestAnimationFrame(() => {
      document.getElementById('otp-0')?.focus();
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setError('Please enter complete OTP');
      setLoading(false);
      return;
    }

    try {
      let sessionMirrorId = mirrorId || localStorage.getItem('mirrorId') || undefined;
      const mirrorCode = localStorage.getItem('mirrorCode');
      if (mirrorCode) {
        try {
          const res = await resolveMirror(mirrorCode);
          sessionMirrorId = res.data?.mirror?.mirrorId || sessionMirrorId;
          if (sessionMirrorId) localStorage.setItem('mirrorId', sessionMirrorId);
          if (res.data?.mirror?.mirrorCode) {
            localStorage.setItem('mirrorCode', res.data.mirror.mirrorCode);
          }
        } catch (_) {
          // verifyOtp will resolve mirror aliases on the server when possible
        }
      }
      const tokenFromQr = qrToken || localStorage.getItem('qrToken') || undefined;
      const response = await verifyOtp(reqId, otpString, mobile, sessionMirrorId, tokenFromQr);

      if (!response?.data?.token || !response?.data?.user) {
        throw new Error(response?.data?.message || 'Invalid response from server');
      }

      const { token, user: userData } = response.data;
      login(token, userData);

      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        throw new Error('Failed to save token');
      }

      await new Promise((resolve) => setTimeout(resolve, 50));

      const isFirstTimeRegistration = userData.isNewUser === true;
      if (isFirstTimeRegistration) {
        navigate('/profile-setup', {
          state: { mirrorId, showWelcomeBonus: true },
          replace: true,
        });
      } else {
        navigate(getCustomerHomeRoute(), { state: { mirrorId }, replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Invalid OTP. Please try again.');
      clearOtpInputs();
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    setResendTimer(60);
    clearOtpInputs();
    await requestOTP();
  };

  if (!mobile || !reqId) {
    return null;
  }

  return (
    <div className="otp-verification-container">
      <div className="card">
        <h1>Verify OTP</h1>
        <p>Enter the 6-digit OTP sent to {mobile}</p>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Safari/iOS autofill target; WebOTP on Chrome uses the hook above. */}
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="otp-autofill-capture"
            aria-hidden="true"
            tabIndex={-1}
            onChange={handleAutofillCapture}
          />

          <div className="otp-input-container">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOTPChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handlePaste(index, e)}
                className="otp-input"
                autoFocus={index === 0}
              />
            ))}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || otp.join('').length !== 6}
          >
            {loading ? 'Verifying...' : 'Verify OTP'}
          </button>

          <div className="resend-section">
            <p>Didn't receive OTP?</p>
            <button
              type="button"
              className="btn-link"
              onClick={handleResend}
              disabled={resendTimer > 0}
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTPVerification;
